// =============================================================================
// Licence helpers — shared utilities across the /licences screens.
// =============================================================================

export type ExpiryBand = "ok" | "warning" | "overdue" | "none";

/**
 * Map an expiry date to a colour band per James's spec:
 *   - overdue  → red   (expired)
 *   - warning  → yellow (within 60 days)
 *   - ok       → no colour (>60 days away)
 *   - none     → no expiry on this licence type
 */
export function expiryBand(expiryDate: string | null | undefined): ExpiryBand {
  if (!expiryDate) return "none";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  expiry.setHours(0, 0, 0, 0);
  const days = Math.floor((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (days < 0) return "overdue";
  if (days <= 60) return "warning";
  return "ok";
}

export function daysToExpiry(expiryDate: string | null | undefined): number | null {
  if (!expiryDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  expiry.setHours(0, 0, 0, 0);
  return Math.floor((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export interface BandStyle {
  /** Tailwind utility classes for a full card background + border */
  cardBg: string;
  /** Text colour class */
  text: string;
  /** Short human label */
  label: string;
}

export function bandStyle(band: ExpiryBand): BandStyle {
  switch (band) {
    case "overdue":
      return {
        cardBg: "border-danger/30 bg-danger/5",
        text:   "text-danger",
        label:  "Overdue",
      };
    case "warning":
      return {
        cardBg: "border-warn/30 bg-warn/5",
        text:   "text-warn",
        label:  "Expiring soon",
      };
    case "ok":
      return {
        cardBg: "",
        text:   "text-ok",
        label:  "Current",
      };
    case "none":
      return {
        cardBg: "",
        text:   "text-text-muted",
        label:  "No expiry",
      };
  }
}

// -----------------------------------------------------------------------------
// Category metadata — 12 categories from migration 022's seed
// -----------------------------------------------------------------------------

export const CATEGORY_DISPLAY: Record<string, string> = {
  safety:          "Safety",
  machinery:       "Machinery — HRWL",
  rigging:         "Dogging & Rigging",
  vehicle:         "Driver & Transport",
  electrical:      "Electrical",
  telco:           "Telco",
  aviation:        "Aviation",
  competency:      "Competency",
  technical:       "Technical",
  admin:           "Admin",
  identification:  "Identification",
  induction:       "Inductions",
};

/**
 * Display order — primary credentials at the top (HRWL rigging tickets,
 * machinery, safety) so they're always front of mind. Inductions go to the
 * bottom — they clog the screen and are rarely needed once loaded.
 * The induction section is also collapsed-by-default in HrLicenceDetailScreen.
 */
export const CATEGORY_ORDER = [
  "rigging",        // Dogging & Rigging — HRWL classes (DG/RB/RI/RA)
  "machinery",      // Machinery — HRWL (WP/forklift/crane/etc)
  "safety",         // First Aid, CPR, Working at Heights, Confined Space
  "vehicle",        // Driver licences, MR/HR/HC, Dangerous Goods
  "electrical",
  "telco",
  "aviation",
  "competency",
  "technical",
  "admin",
  "identification",
  "induction",      // Site-specific inductions (collapsed by default)
] as const;

/**
 * Categories that render collapsed-by-default in detail screens.
 * Inductions in particular pile up — Telstra, NBN, RailCorp, individual
 * client portals — and are noise once loaded.
 */
export const COLLAPSED_BY_DEFAULT: Set<string> = new Set(["induction"]);

// -----------------------------------------------------------------------------
// Date formatting
// -----------------------------------------------------------------------------

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// -----------------------------------------------------------------------------
// Storage path construction
// -----------------------------------------------------------------------------

/**
 * Build the storage path for a licence photo upload.
 * Path shape: {employee_id}/{submission_uuid}/{side}_{timestamp}.{ext}
 *
 * The submission_uuid is generated per-submission (not per-licence) so we can
 * upload photos BEFORE calling app.add_licence — the storage path is the
 * source of truth stored in employee_licences.{front,back}_photo_url.
 */
export function licencePhotoPath(
  employeeId: string,
  submissionUuid: string,
  side: "front" | "back",
  file: File,
): string {
  const ts = Date.now();
  const ext = extFromMime(file.type) ?? fallbackExt(file.name);
  return `${employeeId}/${submissionUuid}/${side}_${ts}.${ext}`;
}

const MIME_EXT: Record<string, string> = {
  "image/jpeg":      "jpg",
  "image/png":       "png",
  "image/webp":      "webp",
  "application/pdf": "pdf",
};

function extFromMime(mime: string): string | null {
  return MIME_EXT[mime] ?? null;
}

function fallbackExt(filename: string): string {
  const dot = filename.lastIndexOf(".");
  if (dot === -1) return "bin";
  return filename.slice(dot + 1).toLowerCase();
}

export const ACCEPTED_PHOTO_MIMES = Object.keys(MIME_EXT).join(",");

/**
 * T-391 — RTO certs come in as PDFs (accepted above), but a PDF fed into an
 * <img> tag renders as a broken tile. Every thumbnail/lightbox must branch on
 * this. Works on raw storage paths AND signed URLs (query string stripped).
 */
export function isPdfPath(pathOrUrl: string | null | undefined): boolean {
  if (!pathOrUrl) return false;
  return pathOrUrl.split("?")[0].toLowerCase().endsWith(".pdf");
}

// =============================================================================
// Filename builder for downloads — T-166 (2026-05-02).
// Matches the HOP photo convention so exports across the platform feel
// consistent. Name first ensures a ZIP folder of multiple holders' licences
// sorts alphabetically by employee.
//   Format:   {FirstName}_{LastName}_{LicenceType}_{ExpiryYYYY-MM-DD}_{FRONT|BACK}.{ext}
//   Example:  Joshua_Thompson_MR_Open_2028-05-17_FRONT.jpg
//   No expiry: Joshua_Thompson_MR_Open_FRONT.jpg
//   No name:   MR_Open_2028-05-17_FRONT.jpg
// Spaces in licence-type display names are converted to underscores by
// safeFilenamePart, which also strips punctuation. ISO date kept hyphenated
// so it visually parses at a glance.
// =============================================================================

import { safeFilenamePart } from "@/lib/storageDownload";

export interface LicenceFilenameInput {
  licence_type_display:  string | null;
  holder_first_name:     string | null;
  holder_last_name:      string | null;
  expiry_date:           string | null;
  side:                  "front" | "back";
  storage_path:          string;
}

export function buildLicenceFilename(i: LicenceFilenameInput): string {
  // Pull extension from the storage path (fallback jpg)
  const ext = (i.storage_path.split(".").pop() ?? "jpg").toLowerCase();

  // Normalise each part — underscores, no punctuation
  const firstPart = safeFilenamePart(i.holder_first_name);
  const lastPart  = safeFilenamePart(i.holder_last_name);
  const namePart  = [firstPart, lastPart].filter(Boolean).join("_");
  const typePart  = safeFilenamePart(i.licence_type_display) || "Licence";
  const expiryPart = i.expiry_date || ""; // already ISO YYYY-MM-DD
  const sideLabel = i.side === "back" ? "BACK" : "FRONT";

  // Compose: name → type → expiry → side
  const segments = [namePart, typePart, expiryPart, sideLabel].filter(Boolean);
  return `${segments.join("_")}.${ext}`;
}
